import { CRMAPI } from '@api/crmApi'
import { CRMAPIManager } from '@classes/crmApiManager'
import { SettingsManager } from '@classes/settingsManager'
import { ErrorHandlingManager } from '@classes/errorHandlingManager'

type RequestStrategy = (cbs: any[], crmId: number) => Promise<any>

const requestStrategies: Record<string, RequestStrategy> = {
    single: async ([callback], crmId) => {
        return await CRMAPIManager.request(api => callback(api, crmId))
    },
  
    multiple: async (callbacks, crmId) => {
        const results = await Promise.allSettled(
            callbacks.map(async (cb) => {
                return await CRMAPIManager.request(api => cb(api, crmId))
            })
        )
        
        // Проверяем ошибки в нескольких запросах
        const errors = results
            .filter(result => result.status === 'rejected')
            .map(rejected => (rejected as PromiseRejectedResult).reason)
            
        // Если обнаружены ошибки, выбрасываем первую из них, но логируем все
        if (errors.length) {
            // Логируем все ошибки для наглядности
            errors.forEach(error => console.error('API Request error:', error))
            
            // Выбрасываем общую ошибку или первую попавшуюся, чтобы блоки catch сработали
            throw new Error(`Множественный API запрос не выполнен. Завершено с ошибкой: (${errors.length}.)`)
        }
        
        return results
    }
}

export async function apiRequest<T>(
    cb: (api: CRMAPI, crmId: number) => Promise<T>
): Promise<T | null>

export async function apiRequest<T extends any[]>(
    ...cbs: { [K in keyof T]: (api: CRMAPI, crmId: number) => Promise<T[K]> }
): Promise<PromiseSettledResult<T[number]>[] | null>

/**
 * Обертка для отправки запросов требующих авторизацию
 */
export async function apiRequest(...cbs: any[]): Promise<any> {
    const { crmID: crmId } = SettingsManager.getConnectionCredentials()

    try {
        const strategy = cbs.length === 1 ? 'single' : 'multiple'
        return await requestStrategies[strategy](cbs, crmId)
    } catch (error) {
        // Централизованная обработка ошибок
        ErrorHandlingManager.handleApiError(error)
    }
}
